import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/interfaces/stores/RootState';
import { Release } from '@/interfaces/models/Release';
import ReleasesApiService from '@/api/http/ReleasesApiService';
import Filter from '@/interfaces/api/Filter';
import { Pagination } from '@/interfaces/api/Pagination';
import { Page } from '@/interfaces/api/Page';
import { AxiosResponse } from 'axios';

interface ReleasesState {
  items: Release[];
  active: Release | null;
  filter: Filter;
  pagination: {
    total: number;
  };
}

const api: ReleasesApiService = new ReleasesApiService();

const state: ReleasesState = {
  items: [],
  active: null,
  filter: {},
  pagination: {
    total: 10,
  },
};

const actions: ActionTree<ReleasesState, RootState> = {
  fetch({ commit, state }, data: { page: Page }) {
    return api.getReleases(data.page, state.filter).then((res: AxiosResponse<Release[] | Pagination<Release>>) => {
      commit('setItems', res.data);
    });
  },
  store({ commit }, data: Partial<Release>) {
    return api.store(data).then((res: AxiosResponse<Release>) => {
      commit('addItem', res.data);
    });
  },
  show({ commit }, data: { id: string }) {
    return api.show(data).then((res: AxiosResponse<Release>) => {
      commit('setActive', res.data);
    });
  },
  update({ commit }, data: Partial<Release>) {
    return api.update(data).then((res: AxiosResponse<Release>) => {
      commit('update', res.data);
    });
  },
  delete({ commit }, data: { id: string }) {
    return api.softDelete(data).then(() => {
      commit('delete', { id: data.id });
    });
  },
  setFilter({ commit }, filter: Filter) {
    commit('setFilter', filter);
  },
};

const mutations: MutationTree<ReleasesState> = {
  setItems(state: ReleasesState, data: Pagination<Release>) {
    state.items = (data.data as Release[]) || data;

    if (data.total) {
      state.pagination.total = data.total;
    }
  },
  setActive(state: ReleasesState, data: Release) {
    state.active = data;
  },
  addItem(state: ReleasesState, data: Release) {
    state.items.push(data);
  },
  update(state: ReleasesState, data: Release) {
    state.items = [...state.items.filter((r: Release) => r._id !== data._id), data];
  },
  delete(state: ReleasesState, data: { id: string }) {
    state.items = [...state.items.filter((r: Release) => r._id !== data.id)];
  },
  setFilter(state: ReleasesState, data: Filter) {
    state.filter = data;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
